// Phase E — Public Offer Share page (no auth).
// Mounted at /offer/:token outside ProtectedRoute. Renders a branded chrome
// + embedded customer-facing PDF preview using <iframe>. Token is the gate.
import React from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, Download, FileText, Building2 } from "lucide-react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "#f0f2f8",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

export default function OfferShare() {
  const { token } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["offer-share", token],
    queryFn: () => axios.get(`${API}/carrier-links/public/offer/${token}`).then((r) => r.data),
    enabled: !!token,
    retry: false,
  });

  const pdfUrl = token ? `${API}/carrier-links/public/offer/${token}/pdf` : "";

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#08090d", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={mono(11, "#9aa1b3", 600, "1.5px")}>LOADING…</div>
      </div>
    );
  }
  if (error || !data) {
    const det = error?.response?.data?.detail || {};
    return (
      <div style={{ minHeight: "100vh", background: "#08090d", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div
          className="rounded-md p-6 max-w-md text-center"
          style={{ background: "#0d0f18", border: "1px solid rgba(226,75,74,0.45)" }}
          data-testid="offer-share-invalid"
        >
          <AlertCircle size={28} style={{ color: "#ff8a8a", margin: "0 auto 10px" }} />
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 18, color: "#ff8a8a", marginBottom: 6 }}>Link unavailable</h1>
          <div style={mono(11, "#cfd5e3")}>
            {det.code === "link_expired" ? "This offer link has expired." : "This offer link is invalid or has been revoked."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#08090d", padding: 16 }} data-testid="offer-share-page">
      <div className="max-w-5xl mx-auto">
        <header
          className="rounded-md p-4 mb-3 flex items-center justify-between flex-wrap gap-3"
          style={{ background: "linear-gradient(90deg, #0d0f18 0%, #1a1322 100%)", border: "1px solid rgba(126,58,168,0.40)" }}
          data-testid="offer-share-header"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-md p-2" style={{ background: "rgba(126,58,168,0.18)", border: "1px solid rgba(126,58,168,0.45)" }}>
              <FileText size={18} color="#c89adb" />
            </div>
            <div>
              <div style={{ ...mono(9, "#9aa1b3", 700, "1.4px"), textTransform: "uppercase" }}>
                {data.deal?.deal_stage === "executed" ? "Delivery confirmation" : "Freight offer"}
              </div>
              <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 20, color: "#f0f2f8" }}>
                {data.deal?.deal_name || data.deal?.id}
              </h1>
              <div style={mono(10, "#cfd5e3")} className="flex items-center gap-2 flex-wrap">
                <Building2 size={11} /> {data.forwarder_name}
                {data.customer_name && <span style={mono(10, "#9aa1b3")}>· {data.customer_name}</span>}
                {data.link?.recipient_label && <span style={mono(10, "#c89adb")} data-testid="offer-share-recipient">for: {data.link.recipient_label}</span>}
              </div>
            </div>
          </div>
          <a
            href={pdfUrl}
            download
            className="inline-flex items-center gap-2 px-3 py-2 rounded"
            style={{ background: "#7e3aa8", color: "#fff", border: "none", ...mono(11, "#fff", 700, "0.8px"), textTransform: "uppercase", textDecoration: "none" }}
            data-testid="offer-share-download"
          >
            <Download size={12} /> Download PDF
          </a>
        </header>

        <div
          className="rounded-md overflow-hidden"
          style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }}
          data-testid="offer-share-preview"
        >
          <iframe
            src={pdfUrl}
            title="Customer offer PDF"
            style={{ width: "100%", height: "calc(100vh - 200px)", minHeight: 540, border: "none", background: "#fff" }}
            data-testid="offer-share-iframe"
          />
        </div>

        <footer className="mt-3 text-center" style={mono(10, "#6b7388")}>
          SITROOM · Automotive Supply-Chain Platform · Powered by {data.forwarder_name}
        </footer>
      </div>
    </div>
  );
}
